/**
 * Helpers puros usados pelo painel/toggle "Usar sugestões NeoFeed" (ver
 * `NeoFeedSuggestionsPanel`). Mantidos fora dos componentes React para
 * facilitar testes isolados (sem depender de React).
 *
 * @typedef {import('../../../types/articles').NeoFeedUsageMode} NeoFeedUsageMode
 * @typedef {import('../../../types/articles').NeoFeedContextSuggestion} NeoFeedContextSuggestion
 * @typedef {import('../../../types/articles').NeoFeedContextSelectionEntry} NeoFeedContextSelectionEntry
 * @typedef {import('../../../types/articles').SelectedNeoFeedContextSource} SelectedNeoFeedContextSource
 */

/** Ordem de preferência ao escolher o modo de uso padrão de uma sugestão. */
const DEFAULT_USAGE_MODE_PRIORITY = ['factual', 'both', 'style'];

export const NEOFEED_USAGE_MODES = /** @type {const} */ (['style', 'factual', 'both']);

export const NEOFEED_USAGE_MODE_LABELS = {
    style: 'Estilo',
    factual: 'Contexto factual',
    both: 'Estilo + fatos',
};

export const NEOFEED_USAGE_MODE_MICROCOPY = {
    style: 'A IA usará esta notícia apenas como referência de linguagem e estrutura.',
    factual: 'A IA poderá usar fatos desta notícia como contexto histórico complementar.',
    both: 'A IA poderá usar tanto o estilo quanto fatos contextuais desta notícia.',
};

/**
 * Modos de uso permitidos para uma sugestão. Se a API não restringir,
 * assume-se que todos os modos são válidos.
 * @param {NeoFeedContextSuggestion} suggestion
 * @returns {NeoFeedUsageMode[]}
 */
export const getAllowedUsageModes = (suggestion) => {
    const allowed = suggestion?.allowed_usage_modes;
    if (Array.isArray(allowed) && allowed.length > 0) {
        return allowed.filter((mode) => NEOFEED_USAGE_MODES.includes(mode));
    }
    return [...NEOFEED_USAGE_MODES];
};

/**
 * Modo de uso padrão ao selecionar uma sugestão: prioriza `factual`, depois
 * `both`, depois `style`, respeitando `allowed_usage_modes` quando presente.
 * @param {NeoFeedContextSuggestion} suggestion
 * @returns {NeoFeedUsageMode}
 */
export const getDefaultUsageMode = (suggestion) => {
    const allowed = getAllowedUsageModes(suggestion);
    const preferred = DEFAULT_USAGE_MODE_PRIORITY.find((mode) => allowed.includes(mode));
    return preferred ?? allowed[0] ?? 'factual';
};

/**
 * Chunks selecionados por padrão ao escolher uma sugestão: todos os chunks
 * retornados pela API.
 * @param {NeoFeedContextSuggestion} suggestion
 * @returns {string[]}
 */
export const getDefaultChunkIds = (suggestion) => (
    Array.isArray(suggestion?.chunks) ? suggestion.chunks.map((chunk) => chunk.chunk_id) : []
);

/**
 * Cria a entrada de seleção inicial para uma sugestão recém-marcada.
 * Quando `forceUsageMode` é informado (painéis simplificados, sem seletor de
 * modo de uso visível), o modo de uso é fixado nesse valor em vez de seguir
 * a prioridade padrão.
 * @param {NeoFeedContextSuggestion} suggestion
 * @param {{ forceUsageMode?: NeoFeedUsageMode }} [options]
 * @returns {NeoFeedContextSelectionEntry}
 */
export const createSelectionEntry = (suggestion, options = {}) => ({
    suggestion,
    selected_chunk_ids: getDefaultChunkIds(suggestion),
    usage_mode: options.forceUsageMode ?? getDefaultUsageMode(suggestion),
});

/**
 * Serializa o mapa de seleção (indexado por `external_article_id`) para o
 * formato esperado por `neofeed_context_sources` no POST /admin/articles/write.
 * Não deve nunca ser chamado se não houver seleção — quem consome já é
 * responsável por só incluir o campo quando o array não estiver vazio.
 * @param {Record<string, NeoFeedContextSelectionEntry>} selectedNeoFeedSources
 * @returns {SelectedNeoFeedContextSource[]}
 */
export const buildNeoFeedContextSourcesPayload = (selectedNeoFeedSources) => (
    Object.entries(selectedNeoFeedSources || {}).map(([externalArticleId, entry]) => ({
        external_article_id: externalArticleId,
        selected_chunk_ids: entry.selected_chunk_ids ?? [],
        usage_mode: entry.usage_mode,
    }))
);

/**
 * Formata o score (0-1) de uma sugestão/chunk como percentual legível.
 * @param {number | null | undefined} score
 * @returns {string | null}
 */
export const formatNeoFeedScore = (score) => {
    if (typeof score !== 'number' || Number.isNaN(score)) return null;
    return `${Math.round(score * 100)}%`;
};
